<script setup lang="ts">
import { userModule } from '@/store/user/module'
import { computed, onMounted, useTemplateRef } from 'vue'
import UIKit from '@pocketprep/ui-kit'

const {
    ModalContainer,
    Button: PocketButton,
} = UIKit

const { numOfAnsweredQuestions, numOfAssignmentQuestions } = defineProps<{
    numOfAnsweredQuestions: number
    numOfAssignmentQuestions: number
}>()

const emit = defineEmits<{
    'close': []
    'submit': []
    'exit': []
}>()

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const titleEl = useTemplateRef('quit-assignment-modal__title')

onMounted(() => {
    setTimeout(() => {
        titleEl.value?.focus()
    }, 500)
})

const emitSubmit = () => {
    emit('submit')
}

const emitExit = () => {
    emit('exit')
}
</script>

<template>
    <Portal to="modal">
        <ModalContainer
            key="quitAssignmentModal"
            :show-close-button="true"
            :is-dark-mode="isDarkMode"
            @close="emit('close')"
        >
            <div class="quit-assignment-modal">
                <h1
                    ref="quit-assignment-modal__title"
                    class="quit-assignment-modal__title"
                    tabindex="-1"
                >
                    Quit Assignment?
                </h1>
                <div
                    v-dark
                    class="quit-assignment-modal__text"
                >
                    You’ve answered {{ numOfAnsweredQuestions }} of
                    {{ numOfAssignmentQuestions }} question{{ numOfAssignmentQuestions > 1 ? 's' : '' }}.
                    You will lose your progress if you quit now.
                    If you submit, unanswered questions will be marked incorrect.
                </div>
                <div class="quit-assignment-modal__actions">
                    <PocketButton
                        type="secondary"
                        class="quit-assignment-modal__submit-btn"
                        :is-dark-mode="isDarkMode"
                        @click="emitSubmit"
                    >
                        Submit Assignment
                    </PocketButton>
                    <PocketButton
                        class="quit-assignment-modal__exit-btn"
                        :is-dark-mode="isDarkMode"
                        @click="emitExit"
                    >
                        Quit Assignment
                    </PocketButton>
                </div>
            </div>
        </ModalContainer>
    </Portal>
</template>

<style lang="scss" scoped>
.quit-assignment-modal {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 35px 41px 26px 38px;
    width: 384px;
    margin: 0 auto;

    &__title {
        font-weight: 600;
        font-size: 18px;
        line-height: 22px;
        margin: 0 0 12px 0;
        outline: none;
    }

    &__text {
        color: $ash;
        font-size: 15px;
        line-height: 22px;
        text-align: center;
        margin-bottom: 29px;

        &--dark {
            color: $fog;
        }
    }

    &__actions {
        display: flex;
    }

    &__submit-btn {
        margin-right: 4px;
    }

    &__submit-btn,
    &__exit-btn {
        padding-left: 12px;
        padding-right: 12px;
    }
}
</style>
